import React from 'react';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Days of week constants: 0=Sunday, 1=Monday, ..., 6=Saturday
export const DAYS_OF_WEEK = [
  { value: 0, labelKey: 'common.daySunday', shortKey: 'common.dayShortSun' },
  { value: 1, labelKey: 'common.dayMonday', shortKey: 'common.dayShortMon' },
  { value: 2, labelKey: 'common.dayTuesday', shortKey: 'common.dayShortTue' },
  { value: 3, labelKey: 'common.dayWednesday', shortKey: 'common.dayShortWed' },
  { value: 4, labelKey: 'common.dayThursday', shortKey: 'common.dayShortThu' },
  { value: 5, labelKey: 'common.dayFriday', shortKey: 'common.dayShortFri' },
  { value: 6, labelKey: 'common.daySaturday', shortKey: 'common.dayShortSat' },
];

interface WeeklyDaySelectorProps {
  value: number[];
  onChange: (days: number[]) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const WeeklyDaySelector: React.FC<WeeklyDaySelectorProps> = ({
  value,
  onChange,
  label,
  description,
  disabled = false,
  showIcon = true,
  size = 'md',
}) => {
  const { t } = useTranslation();

  const defaultLabel = t('layout.ui.workDays');
  const effectiveLabel = label ?? defaultLabel;

  const toggleDay = (day: number) => {
    if (disabled) return;
    
    const newDays = value.includes(day)
      ? value.filter(d => d !== day)
      : [...value, day].sort();
    
    onChange(newDays);
  };

  const selectAll = () => {
    if (disabled) return;
    onChange([0, 1, 2, 3, 4, 5, 6]);
  };

  const selectNone = () => {
    if (disabled) return;
    onChange([]);
  };

  const selectWeekdays = () => {
    if (disabled) return;
    onChange([1, 2, 3, 4, 5]); // Monday-Friday
  };

  // Get button sizes based on size prop
  const buttonSizes = {
    sm: 'w-10 h-10 sm:w-8 sm:h-8 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0',
    md: 'w-10 h-10 sm:w-9 sm:h-9 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0',
    lg: 'w-10 h-10 min-h-[44px] min-w-[44px]',
  };

  const textSize = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  // Helper to get label for selected days
  const getSelectedDaysLabel = () => {
    if (value.length === 0) return t('layout.ui.noDaysSelected');
    if (value.length === 7) return t('layout.ui.allDays');
    if (value.length === 5 && value.includes(1) && value.includes(2) && value.includes(3) && value.includes(4) && value.includes(5)) {
      return t('layout.ui.weekdaysMonfri');
    }
    const dayNames = value.map(d => t(DAYS_OF_WEEK[d].labelKey)).join(', ');
    return dayNames;
  };

  return (
    <div className="space-y-2">
      {/* Label and description */}
      {(effectiveLabel || description) && (
        <div className="flex items-center gap-2">
          {showIcon && <Calendar className="w-4 h-4 text-primary-600" />}
          <div>
            {effectiveLabel && (
              <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {effectiveLabel}
              </label>
            )}
            {description && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {description}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Day buttons */}
      <div className="flex gap-1 flex-wrap">
        {DAYS_OF_WEEK.map((day) => (
          <button
            key={day.value}
            type="button"
            onClick={() => toggleDay(day.value)}
            disabled={disabled}
            aria-pressed={value.includes(day.value)}
            aria-label={t(day.labelKey)}
            className={`
              ${buttonSizes[size]} flex items-center justify-center ${textSize[size]} rounded-lg border transition-colors
              ${value.includes(day.value)
                ? 'border-primary-500 bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 dark:border-primary-400'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            title={t(day.labelKey)}
          >
            {t(day.shortKey)}
          </button>
        ))}
      </div>

      {/* Quick actions */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={selectWeekdays}
          disabled={disabled}
          className={`text-xs text-primary-600 dark:text-primary-400 hover:underline ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {t('common.weekdays')}
        </button>
        <button
          type="button"
          onClick={selectAll}
          disabled={disabled}
          className={`text-xs text-primary-600 dark:text-primary-400 hover:underline ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {t('common.all')}
        </button>
        <button
          type="button"
          onClick={selectNone}
          disabled={disabled}
          className={`text-xs text-gray-500 dark:text-gray-400 hover:underline ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {t('common.none')}
        </button>
      </div>

      {/* Summary */}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('common.selected')}: {getSelectedDaysLabel()}
      </p>
    </div>
  );
};

export default WeeklyDaySelector;
